/**
 * All money in this app is carried as an integer number of minor units
 * (cents/paise). Floats are only ever used at the UI boundary — parsing what
 * the user typed and formatting what they read.
 */

export const MINOR_UNITS_PER_MAJOR = 100;

/** Currency symbols we render without going through Intl (RN's Intl is patchy). */
const SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
  PKR: '₨',
  AED: 'د.إ',
  CAD: 'CA$',
  AUD: 'A$',
  JPY: '¥',
  SGD: 'S$',
  MYR: 'RM',
  ZAR: 'R',
  NGN: '₦',
  BDT: '৳',
  LKR: 'Rs',
  SAR: '﷼',
  TRY: '₺',
  BRL: 'R$',
};

export const SUPPORTED_CURRENCIES = Object.keys(SYMBOLS);

export function currencySymbol(currency: string): string {
  return SYMBOLS[currency.toUpperCase()] ?? currency.toUpperCase();
}

/** `12.34` -> `1234`. Rounds half away from zero. */
export function toMinor(major: number): number {
  if (!Number.isFinite(major)) return 0;
  const scaled = major * MINOR_UNITS_PER_MAJOR;
  return scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
}

/** `1234` -> `12.34`. */
export function toMajor(minor: number): number {
  return minor / MINOR_UNITS_PER_MAJOR;
}

/**
 * Parses free-form user input ("1,234.50", "  12 ", "") into minor units.
 * Returns `null` when the text is not a usable number.
 */
export function parseAmountInput(text: string): number | null {
  const cleaned = text.replace(/[^\d.,-]/g, '').replace(/,/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return null;
  return toMinor(value);
}

/** Minor units -> the plain decimal string a text input should show. */
export function amountInputValue(minor: number): string {
  if (!minor) return '';
  return (Math.abs(minor) / MINOR_UNITS_PER_MAJOR).toFixed(2);
}

/** `1234` + `USD` -> `$12.34`. */
export function formatMoney(minor: number, currency = 'USD'): string {
  const negative = minor < 0;
  const abs = Math.abs(minor);
  const whole = Math.floor(abs / MINOR_UNITS_PER_MAJOR);
  const fraction = abs % MINOR_UNITS_PER_MAJOR;
  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = `${currencySymbol(currency)}${grouped}.${String(fraction).padStart(2, '0')}`;
  return negative ? `-${body}` : body;
}

/** Like `formatMoney` but always carries an explicit sign — used on balances. */
export function formatSignedMoney(minor: number, currency = 'USD'): string {
  if (minor === 0) return formatMoney(0, currency);
  const body = formatMoney(Math.abs(minor), currency);
  return `${minor > 0 ? '+' : '-'}${body}`;
}

/**
 * Splits `totalMinor` across `weights` proportionally, guaranteeing the parts
 * sum back to exactly `totalMinor`.
 *
 * Uses the largest-remainder method: floor every part, then hand the leftover
 * units out one at a time to the parts with the biggest discarded fraction.
 * Ties break on the earlier index so the result is deterministic.
 */
export function allocateByWeights(totalMinor: number, weights: number[]): number[] {
  const n = weights.length;
  if (n === 0) return [];

  const safeWeights = weights.map((w) => (Number.isFinite(w) && w > 0 ? w : 0));
  const totalWeight = safeWeights.reduce((sum, w) => sum + w, 0);

  // No usable weights: fall back to an even split so money is never dropped.
  if (totalWeight <= 0) return allocateEvenly(totalMinor, n);

  const sign = totalMinor < 0 ? -1 : 1;
  const target = Math.abs(totalMinor);

  const exact = safeWeights.map((w) => (target * w) / totalWeight);
  const floors = exact.map((value) => Math.floor(value));
  let remainder = target - floors.reduce((sum, value) => sum + value, 0);

  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  for (let i = 0; remainder > 0; i = (i + 1) % n) {
    floors[order[i].index] += 1;
    remainder -= 1;
  }

  return floors.map((value) => value * sign);
}

/** Even split that still sums to exactly `totalMinor`. */
export function allocateEvenly(totalMinor: number, count: number): number[] {
  if (count <= 0) return [];
  const sign = totalMinor < 0 ? -1 : 1;
  const target = Math.abs(totalMinor);
  const base = Math.floor(target / count);
  let remainder = target - base * count;

  return Array.from({ length: count }, () => {
    const extra = remainder > 0 ? 1 : 0;
    if (remainder > 0) remainder -= 1;
    return (base + extra) * sign;
  });
}

export function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}
